import React, { useState, useEffect } from 'react';
import { X, Smartphone, Laptop, Share, PlusSquare, Check, HelpCircle, Download, Monitor } from 'lucide-react';

interface PwaHandlerProps {
  isStandalone: boolean;
  canInstall: boolean; // Native install prompt support (deferredPrompt != null)
  onInstall: () => void; // Trigger native install
  forceShowGuide?: boolean;
  onCloseGuide?: () => void;
}

const PwaHandler: React.FC<PwaHandlerProps> = ({ 
  isStandalone, 
  canInstall, 
  onInstall,
  forceShowGuide,
  onCloseGuide
}) => {
  const [activeTab, setActiveTab] = useState<'ios' | 'android' | 'desktop'>('desktop');
  const [isMobileDevice, setIsMobileDevice] = useState(false);

  useEffect(() => {
    const checkPlatformAndUa = () => {
      const ua = navigator.userAgent.toLowerCase();
      const isMobileUA = /iphone|ipad|ipod|android|blackberry|mini|windows\sphone|palm/i.test(ua);
      const hasTouch = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
      const mobileStatus = isMobileUA || (hasTouch && window.innerWidth < 1024);
      
      setIsMobileDevice(mobileStatus);

      if (/iphone|ipad|ipod/.test(ua)) {
        setActiveTab('ios');
      } else if (/android/.test(ua)) {
        setActiveTab('android');
      } else {
        setActiveTab('desktop');
      }
    };
    
    checkPlatformAndUa();
  }, []);

  const handleNativeInstallClick = () => {
    onInstall();
  };

  const handleClose = () => {
    if (onCloseGuide) {
      onCloseGuide();
    }
  };

  if (isStandalone) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fade-in select-none">
      <div 
        onClick={(e) => e.stopPropagation()}
        className="relative bg-[#0d0a27] border border-white/10 rounded-[2.5rem] shadow-[0_30px_100px_rgba(0,0,0,0.8)] w-full max-w-md overflow-hidden flex flex-col text-white animate-fade-in-up"
      >
        {/* Header Area */}
        <div className="bg-gradient-to-r from-indigo-950 via-[#0d0a27] to-slate-950 px-6 pt-7 pb-5 border-b border-white/5 relative">
          <button 
            onClick={handleClose}
            className="absolute top-6 right-6 p-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-slate-400 hover:text-white transition-all cursor-pointer"
          >
            <X size={16} />
          </button>
          
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-600/20 border border-indigo-500/30 rounded-xl flex items-center justify-center text-indigo-400 shrink-0">
              <Download size={20} className="animate-bounce" />
            </div>
            <div>
              <h3 className="text-sm font-black uppercase tracking-wider text-[#ffb900]">Instalar Aplicativo</h3>
              <p className="text-[10px] text-slate-300 font-medium leading-none mt-1">Siga as instruções para ter o app nativo no seu dispositivo</p>
            </div>
          </div>
        </div>

        {/* Dynamic Multi-Tab Selector */}
        <div className="px-6 pt-5">
          <div className="bg-black/35 p-1.5 rounded-2xl border border-white/5 grid grid-cols-3 gap-1">
            <button
              onClick={() => setActiveTab('ios')}
              className={`py-2.5 rounded-xl text-[10px] font-black uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                activeTab === 'ios' 
                  ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/25' 
                  : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <Smartphone size={12} /> IPHONE / IOS
            </button>
            <button
              onClick={() => setActiveTab('android')}
              className={`py-2.5 rounded-xl text-[10px] font-black uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                activeTab === 'android' 
                  ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/25' 
                  : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <Smartphone size={12} /> ANDROID
            </button>
            <button
              onClick={() => setActiveTab('desktop')}
              className={`py-2.5 rounded-xl text-[10px] font-black uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                activeTab === 'desktop' 
                  ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/25' 
                  : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <Monitor size={12} /> COMPUTADOR
            </button>
          </div>
        </div>

        {/* Tab Specific Instructions */}
        <div className="p-6 space-y-4 flex-1">
          {activeTab === 'desktop' && (
            <div className="space-y-4 animate-fade-in">
              <div className="flex items-center gap-2.5 mb-1">
                <Laptop size={18} className="text-indigo-400 shrink-0" />
                <h4 className="text-xs font-black uppercase tracking-wider text-slate-100">INSTALAR NO COMPUTADOR</h4>
              </div>
              <p className="text-[10px] text-slate-300 leading-relaxed font-medium">
                Inicie de forma instantânea como um aplicativo nativo exclusivo e com carregamento instantâneo:
              </p>

              <div className="space-y-3">
                {/* Step 1 */}
                <div className="flex gap-3 items-start bg-white/5 p-3.5 rounded-2xl border border-white/5">
                  <div className="w-6 h-6 rounded-lg bg-[#ffb900] text-slate-950 font-black text-xs flex items-center justify-center shrink-0">1</div>
                  <div className="text-[11px]">
                    <p className="font-bold text-white mb-0.5 tracking-wide">
                      Barra de Endereços (+)
                    </p>
                    <p className="text-slate-300 leading-normal">
                      Olhe no topo direito da barra de links (URLs) do seu navegador Chrome, Edge ou Brave. Haverá um ícone de <strong>"+"</strong> ou um símbolo de monitor com uma seta.
                    </p>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="flex gap-3 items-start bg-white/5 p-3.5 rounded-2xl border border-white/5">
                  <div className="w-6 h-6 rounded-lg bg-[#ffb900] text-slate-950 font-black text-xs flex items-center justify-center shrink-0">2</div>
                  <div className="text-[11px]">
                    <p className="font-bold text-white mb-0.5 tracking-wide">
                      Clique em "Instalar"
                    </p>
                    <p className="text-slate-300 leading-normal">
                      Clique sobre ele e selecione a confirmação de instalação para o aplicativo se separar do navegador standard.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'android' && (
            <div className="space-y-4 animate-fade-in">
              <div className="flex items-center gap-2.5 mb-1">
                <Smartphone size={18} className="text-indigo-400 shrink-0" />
                <h4 className="text-xs font-black uppercase tracking-wider text-slate-100">INSTALAR NO ANDROID</h4>
              </div>
              <p className="text-[10px] text-slate-300 leading-relaxed font-medium">
                Adicione o aplicativo diretamente à tela inicial do seu celular para acesso ultra-rápido:
              </p>

              <div className="space-y-3">
                {/* Step 1 */}
                <div className="flex gap-3 items-start bg-white/5 p-3.5 rounded-2xl border border-white/5">
                  <div className="w-6 h-6 rounded-lg bg-[#ffb900] text-slate-950 font-black text-xs flex items-center justify-center shrink-0">1</div>
                  <div className="text-[11px]">
                    <p className="font-bold text-white mb-0.5 tracking-wide">
                      Menu do Chrome / Navegador
                    </p>
                    <p className="text-slate-300 leading-normal">
                      Toque nos <strong>três pontos</strong> no canto superior direito do seu navegador.
                    </p>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="flex gap-3 items-start bg-white/5 p-3.5 rounded-2xl border border-white/5">
                  <div className="w-6 h-6 rounded-lg bg-[#ffb900] text-slate-950 font-black text-xs flex items-center justify-center shrink-0">2</div>
                  <div className="text-[11px]">
                    <p className="font-bold text-white mb-0.5 tracking-wide">
                      Adicionar à Tela Inicial
                    </p>
                    <p className="text-slate-300 leading-normal">
                      Selecione a opção <strong>"Instalar aplicativo"</strong> ou <strong>"Adicionar à tela inicial"</strong> e confirme.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'ios' && (
            <div className="space-y-4 animate-fade-in">
              <div className="flex items-center gap-2.5 mb-1">
                <Smartphone size={18} className="text-indigo-400 shrink-0" />
                <h4 className="text-xs font-black uppercase tracking-wider text-slate-100">INSTALAR NO IPHONE / IPAD</h4>
              </div>
              <p className="text-[10px] text-slate-300 leading-relaxed font-medium">
                O Safari do iOS requer a instalação manual através do menu de compartilhamento:
              </p>

              <div className="space-y-3">
                {/* Step 1 */}
                <div className="flex gap-3 items-start bg-white/5 p-3.5 rounded-2xl border border-white/5">
                  <div className="w-6 h-6 rounded-lg bg-[#ffb900] text-slate-950 font-black text-xs flex items-center justify-center shrink-0">1</div>
                  <div className="text-[11px]">
                    <p className="font-bold text-white mb-0.5 tracking-wide flex items-center gap-1.5">
                      Menu Compartilhar <Share size={12} className="text-[#ffb900]" />
                    </p>
                    <p className="text-slate-300 leading-normal">
                      Toque no botão de <strong>Compartilhar</strong> na barra inferior do Safari.
                    </p>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="flex gap-3 items-start bg-white/5 p-3.5 rounded-2xl border border-white/5">
                  <div className="w-6 h-6 rounded-lg bg-[#ffb900] text-slate-950 font-black text-xs flex items-center justify-center shrink-0">2</div>
                  <div className="text-[11px]">
                    <p className="font-bold text-white mb-0.5 tracking-wide flex items-center gap-1.5">
                      Adicionar à Tela de Início <PlusSquare size={12} className="text-[#ffb900]" />
                    </p>
                    <p className="text-slate-300 leading-normal">
                      Role a lista de opções para baixo e toque em <strong>"Adicionar à Tela de Início"</strong>.
                    </p>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="flex gap-3 items-start bg-white/5 p-3.5 rounded-2xl border border-white/5">
                  <div className="w-6 h-6 rounded-lg bg-emerald-500 text-slate-950 font-black text-xs flex items-center justify-center shrink-0">3</div>
                  <div className="text-[11px]">
                    <p className="font-bold text-white mb-0.5 tracking-wide flex items-center gap-1.5">
                      Confirmar
                    </p>
                    <p className="text-slate-300 leading-normal">
                      Toque em <strong>"Adicionar"</strong> no canto superior direito para finalizar.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Area with Buttons */}
        <div className="bg-[#08061a] p-5 border-t border-white/5 flex flex-col sm:flex-row gap-3 items-center justify-between">
          <div className="w-full sm:w-auto">
            {canInstall && activeTab !== 'ios' && (
              <button
                onClick={handleNativeInstallClick}
                className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-black text-[10px] uppercase tracking-wider py-3 px-5 rounded-xl cursor-pointer transition-all active:scale-95 text-center flex items-center justify-center gap-1.5 hover:scale-[1.02]"
              >
                <Download size={13} /> Instalar Agora Nativamente
              </button>
            )}
          </div>
          
          <button 
            onClick={handleClose}
            className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white font-extrabold text-[10px] uppercase tracking-wider py-3 px-6 rounded-xl cursor-pointer transition-all active:scale-95 text-center"
          >
            ENTENDI, PRONTINHO
          </button>
        </div>
      </div>
    </div>
  );
};

export default PwaHandler;
